import { useEffect, useState } from "react";
import { VehicleStatus, toVehicleStatus } from "@optimusride/client-library/@types/grpc/vehicle_status";
import { db } from "@optimusride/client-library/utils/firebase";
import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import { OperatingArea } from "@optimusride/client-library/@types/grpc/site";
import { distanceBetweenLatLng } from "../../common/utils";
import { captureException } from "../../common/utils/sentry";

const VEHICLE_STATUS_COLLECTION = "vehicle_status";
const VEHICLE_LOCATION_COLLECTION = "vehLocations";

/**
 * Radius to filter out vehicles that belong to a site in miles
 */
const SELECTED_SITE_VEHICLE_RADIUS = 2;

interface VehicleStatusWithID extends VehicleStatus {
    id: string;
}

export interface VehicleLocation {
    coordinates: Coordinates;
    message_timestamp?: string;
    pose?: {
        angle: number;
        x: number;
        y: number;
    };
    updated: string;
    vehicle_id: string;
}

export interface VehicleLocationAndStatus {
    coordinates: Coordinates;
    message_timestamp?: string;
    pose?: {
        angle: number; // angles in radians
        x: number;
        y: number;
    };
    updated: string;
    vehicle_id: string;
    status?: VehicleStatus;
}

export default (selectedSite?: OperatingArea) => {
    const [vehicleLocations, setVehicleLocations] = useState<VehicleLocation[]>([]);
    const [vehicleStatuses, setVehicleStatuses] = useState<VehicleStatusWithID[]>([]);

    useEffect(() => {
        const unsubscribeFromVehicleLocationCollection = db
            .collection(VEHICLE_LOCATION_COLLECTION)
            .onSnapshot((querySnapshot) => {
                const newVehicleLocations: VehicleLocation[] = [];
                querySnapshot.forEach((doc) => {
                    if (doc?.data()?.d) {
                        newVehicleLocations.push(doc.data().d);
                    }
                });
                setVehicleLocations(newVehicleLocations);
            });

        const unsubscribeFromVehicleStatusCollection = db
            .collection(VEHICLE_STATUS_COLLECTION)
            .onSnapshot((querySnapshot) => {
                const newVehicleStatuses: VehicleStatusWithID[] = [];
                querySnapshot.forEach((doc) => {
                    try {
                        newVehicleStatuses.push({
                            id: doc.id,
                            ...toVehicleStatus(doc),
                        });
                    } catch (e) {
                        captureException(e);
                    }
                });

                setVehicleStatuses(newVehicleStatuses);
            });

        return () => {
            unsubscribeFromVehicleStatusCollection();
            unsubscribeFromVehicleLocationCollection();
        };
    }, []);

    // Vehicle location and statuses filtered by their proximity to selected site center
    const vehicleLocationAndStatus: VehicleLocationAndStatus[] = selectedSite?.latLonCenter ? (
        vehicleLocations
            .filter(({ coordinates }) => (
                distanceBetweenLatLng(
                    selectedSite.latLonCenter as Coordinates,
                    coordinates,
                ) <= SELECTED_SITE_VEHICLE_RADIUS))
            .map((vehicleStatus) => ({
                ...vehicleStatus,
                status: (vehicleStatuses
                    .find(({ id }) => id === vehicleStatus.vehicle_id)
                ),
            }))
    ) : [];

    return vehicleLocationAndStatus;
};
